import React, { useState, useCallback } from "react";
import { Download, Plus, TrendingUp, Award, Zap } from "lucide-react";

interface SkillAssessment {
  id: string;
  skill: string;
  expertise: number;
  demand: number;
  passion: number;
  teaching: number;
  competition: number;
  category: string;
}

interface SweetSpotResult {
  skill: string;
  expertise: number;
  demand: number;
  passion: number;
  teaching: number;
  competition: number;
  total: number;
  category: string;
}

const ProfitableSweetSpotFinder: React.FC = () => {
  const [assessments, setAssessments] = useState<SkillAssessment[]>([
    // Professional Skills
    {
      id: "1",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Professional",
    },
    {
      id: "2",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Professional",
    },
    {
      id: "3",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Professional",
    },
    {
      id: "4",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Professional",
    },

    // Life Experiences
    {
      id: "5",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Life Experience",
    },
    {
      id: "6",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Life Experience",
    },
    {
      id: "7",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Life Experience",
    },

    // Nigerian Context
    {
      id: "8",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Nigerian Context",
    },
    {
      id: "9",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Nigerian Context",
    },

    // Hidden Goldmines
    {
      id: "10",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Hidden Goldmine",
    },
    {
      id: "11",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Hidden Goldmine",
    },
    {
      id: "12",
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Hidden Goldmine",
    },
  ]);

  const [showResults, setShowResults] = useState<boolean>(false);
  const [results, setResults] = useState<SweetSpotResult[]>([]);

  const getPlaceholder = (category: string, index: number): string => {
    const placeholders: Record<string, string[]> = {
      Professional: [
        "Microsoft Excel Advanced Functions",
        "Digital Marketing & Social Media",
        "Project Management",
        "Data Analysis & Reporting",
      ],
      "Life Experience": [
        "Weight Loss Journey (Lost 20kg)",
        "Parenting Strategies for Busy Parents",
        "Overcoming Financial Debt",
      ],
      "Nigerian Context": [
        "Navigating Nigerian Business Regulations",
        "Building Professional Networks in Lagos",
      ],
      "Hidden Goldmine": [
        "Organizing Events & Parties",
        "Relationship & Communication Advice",
        "Time Management for Working Mothers",
      ],
    };

    return placeholders[category]?.[index] || "Add your skill/experience...";
  };

  const updateAssessment = useCallback(
    (id: string, field: keyof SkillAssessment, value: string | number) => {
      setAssessments((prev) =>
        prev.map((assessment) =>
          assessment.id === id ? { ...assessment, [field]: value } : assessment
        )
      );
    },
    []
  );

  const addNewAssessment = useCallback(() => {
    const newId = Date.now().toString();
    const newAssessment: SkillAssessment = {
      id: newId,
      skill: "",
      expertise: 0,
      demand: 0,
      passion: 0,
      teaching: 0,
      competition: 0,
      category: "Additional",
    };
    setAssessments((prev) => [...prev, newAssessment]);
  }, []);

  const calculateScores = useCallback(() => {
    const validAssessments = assessments.filter(
      (a) =>
        a.skill.trim() !== "" &&
        (a.expertise > 0 ||
          a.demand > 0 ||
          a.passion > 0 ||
          a.teaching > 0 ||
          a.competition > 0)
    );

    const calculatedResults: SweetSpotResult[] = validAssessments
      .map((assessment) => ({
        ...assessment,
        total:
          assessment.expertise +
          assessment.demand +
          assessment.passion +
          assessment.teaching +
          assessment.competition,
      }))
      .sort((a, b) => b.total - a.total);

    setResults(calculatedResults);
    setShowResults(true);
  }, [assessments]);

  const exportResults = useCallback(() => {
    const csvContent = [
      [
        "Skill/Experience",
        "Expertise",
        "Market Demand",
        "Passion",
        "Teaching Ability",
        "Competition",
        "Total Score",
        "Category",
      ].join(","),
      ...results.map((result) =>
        [
          `"${result.skill}"`,
          result.expertise,
          result.demand,
          result.passion,
          result.teaching,
          result.competition,
          result.total,
          result.category,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my_profitable_sweet_spots.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  }, [results]);

  const getScoreColor = (total: number): string => {
    if (total >= 35) return "bg-green-100 text-green-800 border-green-200";
    if (total >= 25) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    if (total >= 15) return "bg-orange-100 text-orange-800 border-orange-200";
    return "bg-gray-100 text-gray-600 border-gray-200";
  };

  const goldmines = results.filter((r) => r.total >= 35);
  const silvermines = results.filter((r) => r.total >= 25 && r.total < 35);
  const potentialmines = results.filter((r) => r.total >= 15 && r.total < 25);

  const categories = [
    "Professional",
    "Life Experience",
    "Nigerian Context",
    "Hidden Goldmine",
    "Additional",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-white to-purple-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-4">
              🎯 Profitable Sweet Spot Discovery Tool
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Discover Your Million-Naira Intersection in 60 Minutes
            </p>
            <div className="bg-gradient-to-r from-blue-500 to-teal-500 text-white p-6 rounded-2xl font-semibold text-lg">
              Skills + Passion + Market Demand = Your Profitable Sweet Spot
              (Goldmine) 💰
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
          <div className="bg-teal-50 border-l-4 border-teal-400 p-6 rounded-lg">
            <h3 className="font-bold text-teal-800 mb-4">
              📋 How to Use This Tool:
            </h3>
            <ul className="text-teal-700 space-y-2">
              <li>1. Fill in each row with a skill/experience you have</li>
              <li>2. Rate each category from 1-10 (10 being highest)</li>
              <li>3. Click "Calculate My Sweet Spots" to see your goldmines</li>
              <li>4. Focus on items scoring 35+ for immediate monetization</li>
            </ul>
          </div>
        </div>

        {/* Assessment Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
            <h2 className="text-2xl font-bold">
              📊 Your Complete Skills & Passion Assessment
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <tr>
                  <th className="px-4 py-4 text-left font-semibold">
                    Skill/Experience/Knowledge Area
                  </th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Expertise
                    <br />
                    (1-10)
                  </th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Market Demand
                    <br />
                    (1-10)
                  </th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Passion
                    <br />
                    (1-10)
                  </th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Teaching
                    <br />
                    (1-10)
                  </th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Competition
                    <br />
                    (1-10)
                  </th>
                  <th className="px-4 py-4 text-center font-semibold">
                    Total Score
                  </th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => {
                  const categoryAssessments = assessments.filter(
                    (a) => a.category === category
                  );
                  if (categoryAssessments.length === 0) return null;

                  return (
                    <React.Fragment key={category}>
                      <tr>
                        <td
                          colSpan={7}
                          className="bg-gradient-to-r from-gray-100 to-gray-200 px-4 py-3 font-bold text-gray-700"
                        >
                          {category === "Professional" &&
                            "💼 Professional & Technical Skills"}
                          {category === "Life Experience" &&
                            "🎯 Life Experiences & Transformations"}
                          {category === "Nigerian Context" &&
                            "🇳🇬 Nigerian Context & Cultural Bridge Skills"}
                          {category === "Hidden Goldmine" &&
                            "💎 Hidden Goldmines (Things You Take for Granted)"}
                          {category === "Additional" &&
                            "➕ Additional Skills & Experiences"}
                        </td>
                      </tr>
                      {categoryAssessments.map((assessment, index) => {
                        const total =
                          assessment.expertise +
                          assessment.demand +
                          assessment.passion +
                          assessment.teaching +
                          assessment.competition;
                        return (
                          <tr
                            key={assessment.id}
                            className="border-b border-gray-100 hover:bg-gray-50"
                          >
                            <td className="px-4 py-3">
                              <input
                                type="text"
                                value={assessment.skill}
                                onChange={(e) =>
                                  updateAssessment(
                                    assessment.id,
                                    "skill",
                                    e.target.value
                                  )
                                }
                                placeholder={getPlaceholder(category, index)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={assessment.expertise || ""}
                                onChange={(e) =>
                                  updateAssessment(
                                    assessment.id,
                                    "expertise",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={assessment.demand || ""}
                                onChange={(e) =>
                                  updateAssessment(
                                    assessment.id,
                                    "demand",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={assessment.passion || ""}
                                onChange={(e) =>
                                  updateAssessment(
                                    assessment.id,
                                    "passion",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={assessment.teaching || ""}
                                onChange={(e) =>
                                  updateAssessment(
                                    assessment.id,
                                    "teaching",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={assessment.competition || ""}
                                onChange={(e) =>
                                  updateAssessment(
                                    assessment.id,
                                    "competition",
                                    parseInt(e.target.value) || 0
                                  )
                                }
                                className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div
                                className={`px-3 py-1 rounded-lg text-center font-bold border ${getScoreColor(
                                  total
                                )}`}
                              >
                                {total}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Scoring Tips */}
        <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-lg mb-8">
          <h3 className="font-bold text-red-800 mb-4">💡 Scoring Tips:</h3>
          <div className="text-red-700 space-y-2">
            <p>
              <strong>Expertise:</strong> How good are you? (1=Beginner,
              10=Expert)
            </p>
            <p>
              <strong>Market Demand:</strong> How much do people need this in
              Nigeria? (1=No demand, 10=High demand)
            </p>
            <p>
              <strong>Passion:</strong> How much do you enjoy this? (1=Dislike,
              10=Love it)
            </p>
            <p>
              <strong>Teaching Ability:</strong> How well can you explain this
              to others? (1=Poor, 10=Excellent)
            </p>
            <p>
              <strong>Competition:</strong> How many others are doing this?
              (1=Many competitors, 10=Few competitors)
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <button
            onClick={addNewAssessment}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold py-4 px-8 rounded-2xl transition-all transform hover:scale-105 shadow-lg"
          >
            <Plus size={24} />
            Add More Skills/Experiences
          </button>

          <button
            onClick={calculateScores}
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-2xl transition-all transform hover:scale-105 shadow-lg"
          >
            <TrendingUp size={24} />
            🔥 Calculate My Sweet Spots
          </button>
        </div>

        {/* Results Section */}
        {showResults && (
          <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-2xl shadow-2xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold">
                🎉 Your Profitable Sweet Spot Results
              </h2>
              <button
                onClick={exportResults}
                className="flex items-center gap-2 bg-white text-pink-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Download size={20} />
                Export CSV
              </button>
            </div>

            {/* Goldmines */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Award className="text-yellow-300" size={28} />
                Your Goldmines (Score 35-50): Start Here Immediately!
              </h3>
              <div className="space-y-4">
                {goldmines.length > 0 ? (
                  goldmines.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white/20 backdrop-blur-sm p-4 rounded-xl border border-white/30"
                    >
                      <div className="font-bold text-lg">
                        {item.skill} (Score: {item.total}/50)
                      </div>
                      <div className="text-sm opacity-90">
                        Expertise: {item.expertise} | Demand: {item.demand} |
                        Passion: {item.passion} | Teaching: {item.teaching} |
                        Low Competition: {item.competition}
                      </div>
                      <div className="mt-2 italic">
                        💡 Action: Start monetizing this immediately! Create a
                        lead magnet and test demand this week.
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="bg-white/20 p-4 rounded-xl">
                    No goldmines identified yet. Try scoring some of your skills
                    higher!
                  </p>
                )}
              </div>
            </div>

            {/* Silver Mines */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Award className="text-gray-300" size={28} />
                Your Silver Mines (Score 25-34): Develop These Next
              </h3>
              <div className="space-y-4">
                {silvermines.length > 0 ? (
                  silvermines.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white/20 backdrop-blur-sm p-4 rounded-xl border border-white/30"
                    >
                      <div className="font-bold text-lg">
                        {item.skill} (Score: {item.total}/50)
                      </div>
                      <div className="text-sm opacity-90">
                        Expertise: {item.expertise} | Demand: {item.demand} |
                        Passion: {item.passion} | Teaching: {item.teaching} |
                        Low Competition: {item.competition}
                      </div>
                      <div className="mt-2 italic">
                        💡 Action: Develop this skill further and create content
                        to test market interest.
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="bg-white/20 p-4 rounded-xl">
                    Focus on developing your goldmines first.
                  </p>
                )}
              </div>
            </div>

            {/* Potential Mines */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Zap className="text-orange-300" size={28} />
                Potential Opportunities (Score 15-24): Future Development
              </h3>
              <div className="space-y-4">
                {potentialmines.length > 0 ? (
                  potentialmines.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white/20 backdrop-blur-sm p-4 rounded-xl border border-white/30"
                    >
                      <div className="font-bold text-lg">
                        {item.skill} (Score: {item.total}/50)
                      </div>
                      <div className="text-sm opacity-90">
                        Expertise: {item.expertise} | Demand: {item.demand} |
                        Passion: {item.passion} | Teaching: {item.teaching} |
                        Low Competition: {item.competition}
                      </div>
                      <div className="mt-2 italic">
                        💡 Action: Keep this on your radar for future
                        development.
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="bg-white/20 p-4 rounded-xl">
                    Consider exploring more skills and experiences to uncover
                    hidden opportunities.
                  </p>
                )}
              </div>
            </div>

            {/* Monetization Strategies */}
            <div>
              <h3 className="text-2xl font-bold mb-6">
                💰 Monetization Strategies for Your Top Skills:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    title: "🎓 Online Courses",
                    desc: "Create step-by-step video courses teaching your expertise.",
                    price: "₦5,000-₦50,000+",
                  },
                  {
                    title: "👨‍💼 1-on-1 Coaching",
                    desc: "Personal guidance and mentorship.",
                    price: "₦10,000-₦100,000+ per session",
                  },
                  {
                    title: "📚 Digital Products",
                    desc: "Templates, guides, checklists.",
                    price: "₦2,000-₦20,000",
                  },
                  {
                    title: "🏢 Corporate Training",
                    desc: "Train companies and organizations.",
                    price: "₦100,000-₦1,000,000+",
                  },
                  {
                    title: "📝 Consulting",
                    desc: "Strategic advice and problem-solving.",
                    price: "₦20,000-₦200,000+ per project",
                  },
                  {
                    title: "👥 Group Workshops",
                    desc: "In-person or virtual group training.",
                    price: "₦5,000-₦25,000 per person",
                  },
                ].map((strategy, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-sm p-4 rounded-xl border border-white/20"
                  >
                    <h4 className="font-bold mb-2">{strategy.title}</h4>
                    <p className="text-sm mb-2">{strategy.desc}</p>
                    <p className="text-xs font-semibold">
                      Price: {strategy.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfitableSweetSpotFinder;
